import { useContext, useEffect, useMemo } from 'react';
import {
  Stack,
  Typography,
  Grid2 as Grid,
  Box,
  Container
} from '@mui/material';
import BottomNavigation from '../../../components/MenuBottomNavigation';
import MenuSidebarNavigation from '../../../components/MenuSidebarNavigation';
import { WebsiteContext } from '@/pages/website/WebsiteProvider';
import { useFetchMenusQuery } from '@/features/organizations/api/menusApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import MenuCard from '@/pages/menu/components/MenuCard';
import MenuSkeleton from './MenuSkeleton';
import { useResponsive } from '@/hooks';
import './MenuList.scss';
import { useNavigate, useParams } from 'react-router-dom';
import useRoutes from '@/hooks/useRoutes';

const MenuList = () => {
  const { organization } = useContext(WebsiteContext);
  const { isMobile } = useResponsive();
  const { organizationAlias } = useParams<{ organizationAlias: string }>();
  const navigate = useNavigate();
  const [_, generatePath] = useRoutes();
  const menusQuery = useFetchMenusQuery(organization?.id ?? skipToken, {
    skip: !organization?.id
  });

  const sortedMenulist = useMemo(() => {
    if (!Array.isArray(menusQuery.data)) {
      return [];
    }

    return menusQuery.data
      .filter((menu) => Boolean(menu.isPublished))
      .sort((menuA, menuB) => {
        const a = Number(menuA?.order ?? 0);
        const b = Number(menuB?.order ?? 0);

        if (a > b) return +1;
        if (a < b) return -1;
        return 0;
      });
  }, [menusQuery.data]);

  useEffect(() => {
    if (sortedMenulist.length === 1) {
      const url = organizationAlias ? "organizations.menu.read" : "menu.read";
      const path = generatePath(url, { menuId: sortedMenulist[0].id });
      navigate(path);

    }
  }, [sortedMenulist]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#eeeeee', width: '100%' }}>
      {!isMobile && <MenuSidebarNavigation />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isMobile ? '100%' : 'calc(100% - 280px)',
          ml: isMobile ? 0 : '280px',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {menusQuery.isLoading ? (
            <Stack spacing={2}>
              {[1, 2, 3].map((key) => <MenuSkeleton key={key} />)}
            </Stack>
          ) : Array.isArray(menusQuery.data) ? (
            <Stack spacing={2} pb={isMobile ? 12 : 4}>
              <Typography variant="h2">
                Menús disponibles
              </Typography>
              <Grid container spacing={3}>
                {sortedMenulist.map((menu) => (
                  <Grid key={menu.id} size={{ xs: 12, md: 6, lg: 4 }}>
                    <MenuCard {...menu} />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          ) : (
            <Stack spacing={2} justifyContent={'center'} alignItems={'center'} sx={{ flexGrow: 1, py: 10 }}>
              <Typography fontSize="4rem">🌵</Typography>
              <Typography variant="h2" color="black">
                Lo sentimos
              </Typography>
              <Typography variant="body1">
                Por el momento no tenemos menús disponibles
              </Typography>
            </Stack>
          )}
        </Container>
        {isMobile && <BottomNavigation />}
      </Box>
    </Box>
  );
};

export default MenuList;
